import { StatusChip } from "../../../../common/chips/status/StatusChip";
import { useTranslation } from "react-i18next";
import { BadgeStatuses } from "../../../../../common/enums";
import { CompanyBadgesTableRow, DatatableRowMetadata, SelectedBadgeRows } from "../../../types";
import { IPersonBadge, IProduct } from "../../../../../common/types";
import { useState } from "react";
import { Display, MUIDataTableCheckboxProps, MUIDataTableMeta } from "mui-datatables";
import { UserWithAvatar } from "../components/userWithAvatar/UserWithAvatar";
import { StatusTableRow } from "../components/tableRow/StatusTableRow";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../../../../routes";
import BadgeHelper from "../../../../../common/network/helpers/badges/BadgeHelper";
import camelCase from "lodash.camelcase";
import { useSelector } from "react-redux";
import {
  badgeOrdersSelector,
  badgesSelector,
  eventEditionSelector
} from "../../../../../redux/eventEdition/selectors/eventEdition.selectors";
import { BadgeStatusBar } from "../components/badgeStatusBar/BadgeStatusBar";
import { productsSelector } from "../../../../../redux/magento/selectors/magento.selectors";
import { BadgeOrder } from "../../../../../redux/types";
import { countryListSelector } from "../../../../../redux/country/selectors/country.selectors";

export const useCompanyBadgesTableConfig = (
  tableData: CompanyBadgesTableRow[],
  resetTableData: (data: CompanyBadgesTableRow[]) => void
) => {
  const { t } = useTranslation("common");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<SelectedBadgeRows>({ badgeIds: [], badges: [] });
  const { eventEdition } = useSelector(eventEditionSelector);
  const orders: BadgeOrder[] = useSelector(badgeOrdersSelector);
  const products: IProduct[] = useSelector(productsSelector);
  const badges = useSelector(badgesSelector);
  const countries = useSelector(countryListSelector);

  const history = useNavigate();

  const routeToBadge = (badge: IPersonBadge) => {
    history(`${Routes.badges}/${badge.id}`, { state: { selectedBadge: badge } });
  };

  const columns = [
    {
      name: t("companyPage.table.name"),
      options: {
        customBodyRender: (val, tableMeta: MUIDataTableMeta) => {
          const badge = tableMeta.rowData[tableMeta.rowData.length - 1];

          return (
            <StatusTableRow status={getBadgeStatusByIndex(tableMeta.rowIndex)}>
              <UserWithAvatar name={val} onClick={() => routeToBadge(badge)} />
              <BadgeStatusBar
                badge={badge}
                eventEdition={eventEdition}
                countries={countries}
                orders={orders}
                products={products}
                badges={badges}
              />
            </StatusTableRow>
          );
        }
      }
    },
    {
      name: t("companyPage.table.badgeStatus"),
      options: {
        customBodyRender: (val, tableMeta: MUIDataTableMeta) => {
          return (
            <StatusTableRow status={getBadgeStatusByIndex(tableMeta.rowIndex)}>
              <StatusChip status={val} />
            </StatusTableRow>
          );
        }
      }
    },
    {
      name: t("companyPage.table.customerType"),
      options: {
        customBodyRender: (val, tableMeta: MUIDataTableMeta) => {
          return (
            <StatusTableRow status={getBadgeStatusByIndex(tableMeta.rowIndex)}>
              <span>{val}</span>
            </StatusTableRow>
          );
        }
      }
    },
    {
      name: t("companyPage.table.badgeProduct"),
      options: {
        customBodyRender: (val, tableMeta: MUIDataTableMeta) => {
          return (
            <StatusTableRow status={getBadgeStatusByIndex(tableMeta.rowIndex)}>
              <span>{val}</span>
            </StatusTableRow>
          );
        }
      }
    },
    {
      name: t("companyPage.table.paymentStatus"),
      options: {
        customBodyRender: (val, tableMeta: MUIDataTableMeta) => {
          return (
            <StatusTableRow status={getBadgeStatusByIndex(tableMeta.rowIndex)}>
              <StatusChip
                status={val}
                label={t(`paymentStatuses.${camelCase(BadgeHelper.isBadgePaymentStatusValid(val) ? val.toLowerCase() : "dataNotFound")}`)}
              />
            </StatusTableRow>
          );
        }
      }
    },
    { name: "id", options: { display: "excluded" as Display } }
  ];

  const deselectRows = () => {
    setSelectedRows({ badgeIds: [], badges: [] });
  };

  const getBadgeStatusByCheckboxProps = (props: MUIDataTableCheckboxProps) => {
    if (props["data-description"] === "row-select-header") {
      return BadgeStatuses.INACTIVE;
    }
    return getBadgeStatusByIndex(props["data-index"]!);
  };

  const getBadgeStatusByIndex = (index?: number) => {
    if (!index && index !== 0) {
      return getBadgesByIndexes([0])[0]?.status;
    }
    return getBadgesByIndexes([index])[0]?.status;
  };

  const extractBadgeFromRow = (row: CompanyBadgesTableRow): IPersonBadge | undefined => {
    const badgeCandidate = row[row.length - 1];
    return typeof badgeCandidate === "object" ? (badgeCandidate as IPersonBadge) : undefined;
  };

  const getBadgesByIndexes = (indexes: number[]) => {
    return indexes.map(idx => extractBadgeFromRow(tableData[idx]!)).filter(Boolean) as IPersonBadge[];
  };

  const getIndexesFromBadgeIds = (badgeIds: string[]) => {
    if (!badgeIds.length) return [];

    return tableData.reduce<number[]>((acc, row, idx) => {
      const badge = extractBadgeFromRow(row);
      if (badge && badgeIds.includes(badge.id)) {
        acc.push(idx);
      }
      return acc;
    }, []);
  };

  const options = {
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    filter: false,
    pagination: true,
    rowsSelected: getIndexesFromBadgeIds(selectedRows.badgeIds),
    onTableChange: (action, tableState) => {
      if (action === "changeRowsPerPage" || action === "sort") {
        resetTableData(tableState.data.map(r => r.data));
      }
    },
    onRowSelectionChange: (_: string[][], rowMeta: DatatableRowMetadata[]) => {
      const indexes = rowMeta.map(row => row.dataIndex);
      const badges = getBadgesByIndexes(indexes);
      const badgeIds = badges.map(b => b.id);
      setSelectedRows({ badgeIds, badges });
    },
    isRowSelectable: dataIndex => {
      const row = tableData[dataIndex]!;
      const badge = row[Object.keys(row).length - 1] as IPersonBadge;
      return badge?.status !== BadgeStatuses.CANCELLED;
    },
    rowsPerPage,
    onChangeRowsPerPage: (numberOfRows: number) => setRowsPerPage(numberOfRows),
    textLabels: {
      body: {
        noMatch: t("companyPage.table.noMatch")
      },
      pagination: {
        rowsPerPage: t("companyPage.table.rowsPerPage")
      }
    }
  };

  return {
    columns,
    options,
    selectedRows,
    deselectRows,
    getBadgeStatusByIndex,
    getBadgesByIndexes,
    getBadgeStatusByCheckboxProps,
    getIndexesFromBadgeIds
  };
};
